import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@client/components/ui/dialog';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { Label } from '@client/components/ui/label';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Skeleton } from '@client/components/ui/skeleton';
import { AlertCircle, Send, Wand2 } from 'lucide-react';
import { formatCurrency } from '@shared/formatCurrency';
import type { DialogType } from './useSubscriptionSeats';

// Helper function to format date as "MMM d, yyyy" (e.g., "Jan 1, 2025")
function formatDate(date: Date | null | undefined): string {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Helper function to format date range (e.g., "Dec 16, 2025 - Jan 16, 2026")
function formatDateRange(start: Date | null | undefined, end: Date | null | undefined): string {
  if (!start || !end) return '';
  const startStr = new Date(start).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const endStr = new Date(end).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startStr} - ${endStr}`;
}

// Helper function to calculate days remaining in billing cycle
function calculateDaysRemaining(
  periodStart: Date | null | undefined,
  periodEnd: Date | null | undefined,
): number | null {
  if (!periodEnd) return null;
  const start = periodStart ? new Date(periodStart) : new Date();
  const end = new Date(periodEnd);
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

interface InvitationPreview {
  advisorExists: boolean;
  advisorInfo?: {
    advisorId: string;
    advisorFirstName: string | null;
    advisorLastName: string | null;
    advisorEmail: string;
  };
  billingPreview: BillingImpact;
}

interface SeatDialogsProps {
  dialogType: DialogType;
  emailInput: string;
  setEmailInput: (value: string) => void;
  showPreview: boolean;
  setShowPreview: (value: boolean) => void;
  invitationPreviewStatus: 'idle' | 'loading' | 'success' | 'error';
  isPreviewingInvitation: boolean;
  invitationPreviewError: string | null;
  duplicateEmailError: string | null;
  invitationPreview: InvitationPreview | null;
  isMutating: boolean;
  currentMutation: string | null;
  isWizardUser?: boolean;
  onClose: () => void;
  onPreviewInvitation: () => void;
  onSendInvitation: () => void;
  onRemoveSeat: () => void;
  onDirectAssignSeat?: () => void;
}

export function SeatDialogs({
  dialogType,
  emailInput,
  setEmailInput,
  showPreview,
  setShowPreview,
  invitationPreviewStatus,
  isPreviewingInvitation,
  invitationPreviewError,
  duplicateEmailError,
  invitationPreview,
  isMutating,
  currentMutation,
  isWizardUser,
  onClose,
  onPreviewInvitation,
  onSendInvitation,
  onRemoveSeat,
  onDirectAssignSeat,
}: SeatDialogsProps) {
  return (
    <>
      {/* Invite Advisor Dialog */}
      <Dialog
        open={dialogType === 'addSeat'}
        onOpenChange={onClose}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-primary-dark">Invite Advisor to Subscription</DialogTitle>
            <DialogDescription className="text-gray-600">
              {!showPreview
                ? "Enter the advisor's email to see billing impact and send an invitation."
                : 'Review the details below and confirm to send the invitation.'}
            </DialogDescription>
          </DialogHeader>

          {!showPreview ? (
            <>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label
                    htmlFor="email"
                    className="text-primary-dark"
                  >
                    Advisor Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="advisor@example.com"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && emailInput) {
                        onPreviewInvitation();
                      }
                    }}
                  />
                </div>
                {duplicateEmailError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{duplicateEmailError}</AlertDescription>
                  </Alert>
                )}
                {invitationPreviewError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{invitationPreviewError}</AlertDescription>
                  </Alert>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  onClick={onPreviewInvitation}
                  disabled={!emailInput || isPreviewingInvitation}
                >
                  {isPreviewingInvitation ? 'Loading...' : 'Preview Invitation'}
                </Button>
              </DialogFooter>
            </>
          ) : invitationPreviewStatus === 'loading' ? (
            // Skeleton loading state
            <div className="grid gap-4 py-4">
              <div className="rounded-md border p-4">
                <Skeleton className="mb-2 h-4 w-24" />
                <Skeleton className="h-4 w-48" />
              </div>
              <div className="rounded-md border p-4">
                <Skeleton className="mb-3 h-4 w-24" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
              <Skeleton className="h-16 w-full rounded-md" />
            </div>
          ) : invitationPreview ? (
            <>
              <div className="grid gap-4 py-4">
                {/* Advisor Status */}
                <div className="rounded-md border p-4">
                  <h4 className="text-primary-dark mb-2 text-sm font-medium">Advisor Status</h4>
                  {invitationPreview.advisorExists ? (
                    <div className="text-primary flex items-center gap-2 text-sm">
                      <div className="bg-primary h-2 w-2 rounded-full" />
                      <span>Existing Advisor</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      <span>New Advisor</span>
                    </div>
                  )}
                </div>

                {/* Billing Impact */}
                <div className="rounded-md border bg-white">
                  {/* Section 1: Current Monthly Subscription */}
                  <div className="flex items-start justify-between p-4">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Current Total</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        {invitationPreview.billingPreview.currentQuantity} advisor{' '}
                        {invitationPreview.billingPreview.currentQuantity === 1 ? 'seat' : 'seats'}
                      </p>
                    </div>
                    <span className="text-base font-semibold text-gray-900">
                      {formatCurrency(invitationPreview.billingPreview.currentMonthlyCharge, 2)}
                    </span>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-200" />

                  {/* New Monthly Subscription */}
                  <div className="flex items-start justify-between p-4">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900">
                        New Total
                        {invitationPreview.billingPreview.nextInvoiceDate && (
                          <>
                            {' '}
                            (Starting {formatDate(invitationPreview.billingPreview.nextInvoiceDate)}
                            )
                          </>
                        )}
                      </h4>
                      <p className="mt-1 text-xs text-gray-500">
                        {invitationPreview.billingPreview.newQuantity} advisor{' '}
                        {invitationPreview.billingPreview.newQuantity === 1 ? 'seat' : 'seats'} (
                        {formatCurrency(invitationPreview.billingPreview.unitPricePerSeat, 2)} ×{' '}
                        {invitationPreview.billingPreview.newQuantity})
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-base font-semibold text-gray-900">
                        {invitationPreview.billingPreview.nextInvoiceAmount
                          ? formatCurrency(invitationPreview.billingPreview.nextInvoiceAmount, 2)
                          : formatCurrency(invitationPreview.billingPreview.newMonthlyCharge, 2)}
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-200" />

                  {/* Charge Upon Acceptance */}
                  <div className="flex items-start justify-between p-4">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900">Due Upon Acceptance</h4>
                      {(() => {
                        const daysRemaining = calculateDaysRemaining(
                          invitationPreview.billingPreview.prorationDate,
                          invitationPreview.billingPreview.billingPeriodEnd,
                        );
                        const dateRange = formatDateRange(
                          invitationPreview.billingPreview.prorationDate,
                          invitationPreview.billingPreview.billingPeriodEnd,
                        );
                        return (
                          <p className="mt-1 text-xs italic text-gray-500">
                            {daysRemaining !== null
                              ? `Prorated for ${daysRemaining} day${
                                  daysRemaining !== 1 ? 's' : ''
                                } remaining in current billing cycle`
                              : 'Prorated for remaining days in current billing cycle'}
                            {dateRange && <> ({dateRange})</>}
                          </p>
                        );
                      })()}
                    </div>
                    <span className="text-primary-dark text-base font-semibold">
                      {formatCurrency(invitationPreview.billingPreview.prorationAmount, 2)}
                    </span>
                  </div>
                </div>

                {/* Invitation Email */}
                <div className="bg-primary-50 border-primary-100 rounded-md border p-4">
                  <p className="text-primary-dark text-sm">
                    An invitation email will be sent to <strong>{emailInput}</strong>.
                    {invitationPreview.advisorExists
                      ? ' They can click accept in the email to join your subscription.'
                      : ' They must create an account using this exact email address to receive the subscription.'}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <div className="flex w-full justify-between">
                  <div className="flex gap-2">
                    {/* Wizard Direct Assignment - Only show if advisor exists */}
                    {isWizardUser && invitationPreview.advisorExists && (
                      <Button
                        onClick={onDirectAssignSeat}
                        disabled={isMutating && currentMutation === 'direct_assign'}
                        variant="ghost"
                        className="text-primary-dark"
                      >
                        <Wand2 className="mr-1 h-4 w-4" />
                        {isMutating && currentMutation === 'direct_assign'
                          ? 'Assigning...'
                          : 'Assign Directly'}
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowPreview(false)}
                    >
                      Back
                    </Button>
                    <Button
                      onClick={onSendInvitation}
                      disabled={isMutating && currentMutation === 'invite'}
                    >
                      <Send className="mr-1 h-4 w-4" />
                      {isMutating && currentMutation === 'invite'
                        ? 'Sending...'
                        : 'Send Invitation'}
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Remove Seat Dialog */}
      <Dialog
        open={dialogType === 'removeSeat'}
        onOpenChange={onClose}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-primary-dark">Remove Seat</DialogTitle>
            <DialogDescription className="text-gray-600">
              Are you sure you want to remove this seat? If the seat is active, the advisor will
              lose access and your subscription quantity will be decreased. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onRemoveSeat}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
